import { CheckField, CheckGroupField } from '@ff-it/form';
import { Box, openModal, RenderProps, Stack } from '@ff-it/ui';
import { BlockWidget, DialogForm } from 'components';
import { api } from 'services';
import { type ReactNode } from 'react';
import invariant from 'tiny-invariant';
import { ListBlock } from 'modules/campaign/block/types';
import { PlanStore } from '../state';
import { cancelExport } from '../const';
import { isSameMonth, parseISO } from 'date-fns';

type ExportOptions = {
  blocks: number[];
  show_groups: boolean;
  show_month_columns: boolean;
  show_month_sheets: boolean;
  hidden_columns: string[];
};

const intialValues: ExportOptions = {
  blocks: [],
  show_groups: true,
  show_month_columns: true,
  show_month_sheets: true,
  hidden_columns: [],
};

const hidableColumns = [
  { value: 'comment', label: 'Comments' },
  { value: 'client_factor', label: 'Factors' },
];

function PlanExportOptionsDialog({
  siblings,
  hasGroups,
  hasMonths,
  ...props
}: RenderProps<ExportOptions | undefined> & {
  siblings: ListBlock[];
  hasGroups: boolean;
  hasMonths: boolean;
}) {
  return (
    <DialogForm {...props} title="Plan export" initialValues={intialValues}>
      {siblings.length > 0 && (
        <Box borderBottom="subtle" mb="3" pb="3">
          <CheckGroupField
            name="blocks"
            className="mb-0"
            options={siblings.map((block) => ({
              value: block.id.toString(),
              label: (<BlockWidget {...block} flat />) as ReactNode,
            }))}
            label="Include sibling plans"
          />
        </Box>
      )}
      <CheckGroupField label="Hide columns" name="hidden_columns" options={hidableColumns} />
      <Stack>
        {hasGroups && <CheckField name="show_groups" fieldLabel="Show group rows" variant="switch" />}
        {hasMonths && <CheckField name="show_month_columns" fieldLabel="Show month columns" variant="switch" />}
        {hasMonths && <CheckField name="show_month_sheets" fieldLabel="Show month sheets" variant="switch" />}
      </Stack>
    </DialogForm>
  );
}

export async function promptExportPlanOptions({
  campaign_id,
  id,
  groups,
  date_from,
  date_to,
  plan_strategy,
}: PlanStore): Promise<ExportOptions | undefined | typeof cancelExport> {
  if (plan_strategy === 'NAIVE') {
    return undefined;
  }
  const res = await api.request<ListBlock[], unknown>({
    method: 'GET',
    url: 'campaign/blocks/',
    queryParams: {
      campaign: campaign_id,
      exclude: id,
      plan_strategy: plan_strategy,
      ordering: 'order',
    },
  });
  invariant(res.ok);

  const hasGroups = Object.keys(groups).length > 0;
  const hasMonths = !isSameMonth(parseISO(date_from), parseISO(date_to));
  const sibilings = res.data;

  let exportOptions;
  if (sibilings.length || hasGroups) {
    exportOptions = await openModal<ExportOptions>(
      (props) => (
        <PlanExportOptionsDialog siblings={sibilings} hasGroups={hasGroups} hasMonths={hasMonths} {...props} />
      ),
      {
        testId: 'plan-export-options',
      },
    );
    if (typeof exportOptions === 'undefined') {
      return cancelExport;
    }
    return exportOptions;
  }
}
